import type { SessionUser } from '@zilian/contracts';

// Production builds call the API domain directly (VITE_API_BASE_URL from infra/env/<env>/build.env);
// local development leaves it unset and goes through the Vite /api proxy.
const apiBase = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api`;

let user: SessionUser | null = null;
let workspaceId = '';
export function configureApi(nextUser: SessionUser | null, nextWorkspace: string) {
  user = nextUser;
  workspaceId = nextWorkspace;
}
/** `stale`: the record changed since it was loaded; refresh instead of retrying. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly stale = false,
    /** HTTP status; 0 when the server could not be reached. */
    readonly status = 0,
  ) {
    super(message);
  }
}

export async function api<T = any>(
  path: string,
  body?: unknown,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = { 'x-workspace-id': workspaceId };
  if (user?.csrfToken) headers['x-csrf-token'] = user.csrfToken;
  if (body !== undefined && !(body instanceof FormData))
    headers['Content-Type'] = 'application/json';
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    ...options,
    method: body === undefined ? 'GET' : 'POST',
    headers: { ...headers, ...options.headers },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '网络连接异常' }));
    throw new ApiError(
      error.message ?? `请求失败 ${response.status}`,
      !!error.stale,
      response.status,
    );
  }
  return response.json();
}
export async function download(path: string, name: string) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: { 'x-workspace-id': workspaceId },
  });
  if (!response.ok) throw new Error('下载失败或没有权限');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

/** Loads an authenticated image (e.g. a collection QR code) as a local object URL. */
export async function imageUrl(path: string) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    headers: { 'x-workspace-id': workspaceId },
  });
  if (!response.ok) throw new Error('图片加载失败或没有权限');
  return URL.createObjectURL(await response.blob());
}
